import React, { useState } from "react";
import {
  PlxsCallout,
  PlxsDatePicker,
  PlxsRadioButtonGroup,
} from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";
import { getCalloutConfig } from "../../utils/wizardContentRegistry";

interface SchedulingStepProps {
  drawData: DrawData;
  updateDrawData: (updates: Partial<DrawData>) => void;
  onNext: () => void;
  onBack: () => void;
  isLocked?: boolean;
}

const SchedulingStep: React.FC<SchedulingStepProps> = ({
  drawData,
  updateDrawData,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const scrutineerRequired =
    drawData.scrutineerSettings?.scrutineerRequired === true;

  const scheduledCallout = getCalloutConfig("schedulingScheduled");
  const scrutineerCallout = getCalloutConfig("schedulingDisabled");

  // Radio button state: "immediately" or "schedule"
  const [schedulingOption, setSchedulingOption] = useState<
    "immediately" | "schedule"
  >(drawData.scheduledDateTime ? "schedule" : "immediately");

  const handleSchedulingOptionChange = (option: "immediately" | "schedule") => {
    setSchedulingOption(option);
    if (option === "immediately") {
      // Clear scheduled date/time when choosing immediate execution
      updateDrawData({ scheduledDateTime: undefined });
      setErrorMessage("");
    }
  };

  const handleDateTimeSelect = (date: Date | undefined) => {
    // Always update the drawData with the selected date (even if invalid)
    // This retains the user's input in the field
    updateDrawData({ scheduledDateTime: date });

    if (date) {
      const now = new Date();
      // Only reject if the date is more than 5 seconds in the past
      // This accounts for the time picker setting current seconds/milliseconds
      const minAllowedTime = new Date(now.getTime() - 5000); // 5 seconds ago
      
      // Check if the selected date is clearly in the past
      if (date < minAllowedTime) {
        setErrorMessage("Please select a date and time in the future.");
        return;
      }
    }
    
    // Clear error if date is valid or undefined
    setErrorMessage("");
  };

  // Check if we can proceed
  const canProceed = () => {
    // If scheduling is selected, must have a valid date/time with no errors
    if (schedulingOption === "schedule") {
      if (!drawData.scheduledDateTime || errorMessage) {
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    // Validate when scheduling is selected
    if (schedulingOption === "schedule") {
      // Show error if no date/time chosen
      if (!drawData.scheduledDateTime) {
        setErrorMessage("Please select a date and time.");
        return;
      }
      
      // Validate that scheduled date is not clearly in the past
      const now = new Date();
      const minAllowedTime = new Date(now.getTime() - 5000); // 5 seconds ago
      
      if (drawData.scheduledDateTime < minAllowedTime) {
        setErrorMessage("Please select a date and time in the future.");
        return;
      }
    }
    
    onNext();
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        padding: "0",
        boxSizing: "border-box",
      }}
    >
      <WizardStepHeader
        title="When should it run?"
        description="Run it now, or set a time and we'll handle the rest."
      />

      {/* Radio Button Group for Scheduling Options */}
      <div
        style={{
          marginBottom: "var(--plexus-space-lg)",
          backgroundColor: "var(--plexus-color-surface-50)",
          padding: "var(--plexus-space-lg)",
          borderRadius: "var(--plexus-space-xs)",
          border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
        }}
      >
        <div
          style={{
            fontSize: "1rem",
            fontWeight: 600,
            marginBottom: "var(--plexus-space-md)",
            color: "var(--plexus-color-neutral-100)",
          }}
        >
          Run
        </div>

        <div>
          <style>{`
            [data-testid="scheduling-option-radio-group"] fieldset {
              border: none !important;
              margin: 0 !important;
              margin-bottom: 0 !important;
              padding: 0 !important;
            }
            
            [data-testid="scheduling-option-radio-group"] {
              border: none !important;
              margin: 0 !important;
              margin-bottom: 0 !important;
            }
            
            [data-testid="scheduling-option-radio-group"] > div {
              padding: 0 !important;
              margin: 0 !important;
              margin-bottom: 0 !important;
            }
            
            /* Remove any makeStyles margin-bottom applied by Plexus */
            [data-testid="scheduling-option-radio-group"] [class*="makeStyles-marginBottom"] {
              margin-bottom: 0 !important;
            }
            
            /* Force vertical layout for scheduling radio group */
            [data-testid="scheduling-option-radio-group"] fieldset > div {
              display: flex !important;
              flex-direction: column !important;
              gap: var(--plexus-space-md, 16px) !important;
            }
            
            [data-testid="scheduling-option-radio-group"] label {
              width: 100% !important;
              margin-bottom: var(--plexus-space-sm, 8px) !important;
            }
          `}</style>
          <PlxsRadioButtonGroup
            dataTestId="scheduling-option-radio-group"
            name="scheduling-option"
            options={[
              {
                value: "immediately",
                label: (
                  <span>
                    Run immediately
                    <span
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: 400,
                        marginTop: "2px",
                        color: "var(--plexus-color-neutral-300)",
                      }}
                    >
                      Fastest path to a result
                    </span>
                  </span>
                ),
              },
              {
                value: "schedule",
                label: "Schedule the draw for later",
              },
            ]}
            value={scrutineerRequired ? "immediately" : schedulingOption}
            isDisabled={scrutineerRequired || isLocked}
            onChange={(value: string) => {
              if (!scrutineerRequired && !isLocked) {
                handleSchedulingOptionChange(
                  value as "immediately" | "schedule"
                );
              }
            }}
          />
        </div>

        {scrutineerRequired ? (
          <div style={{ marginTop: "var(--plexus-space-md)" }}>
            <PlxsCallout
              tone={scrutineerCallout.tone}
              message={scrutineerCallout.message}
              supportingCopy={scrutineerCallout.supportingCopy}
              dataTestId={scrutineerCallout.dataTestId ?? "scheduling-disabled-callout"}
            />
          </div>
        ) : (
          schedulingOption === "schedule" && (
            <div style={{ marginTop: "var(--plexus-space-md)" }}>
              <PlxsCallout
                tone={scheduledCallout.tone}
                message={scheduledCallout.message}
                supportingCopy={scheduledCallout.supportingCopy}
                dataTestId={scheduledCallout.dataTestId ?? "scheduling-covered-callout"}
              />
            </div>
          )
        )}
      </div>

      {/* Date Picker - only show when "schedule" is selected and scrutineer is not required */}
      {schedulingOption === "schedule" && !scrutineerRequired && (
        <div
          style={{
            marginBottom: "var(--plexus-space-lg)",
            backgroundColor: "var(--plexus-color-surface-50)",
            padding: "var(--plexus-space-lg)",
            borderRadius: "var(--plexus-space-xs)",
            border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
          }}
        >
          <style>{`
            /* AM/PM period button - ensure it has space and is fully visible */
            button[data-testid="datepicker-single--time-picker--selector--time-input--period--button"] {
              margin-left: 32px !important;
              padding: 8px 16px !important;
              min-width: 50px !important;
              flex-shrink: 0 !important;
              white-space: nowrap !important;
            }
          `}</style>
          <PlxsDatePicker
            dataTestId="datepicker-single"
            hasError={!!errorMessage}
            errorMessage={errorMessage}
            instructionText="Click on the calendar button or start typing"
            isDefaultActive={false}
            isDisabled={isLocked}
            label="Select a date and time"
            secondaryLabel=""
            secondaryLabelUrl=""
            onSelect={handleDateTimeSelect}
            selected={drawData.scheduledDateTime}
            locale="en-AU" // Use Australian English locale for date formatting
            isTimeEditable
          />
        </div>
      )}

      <WizardFooterNav
        backButton={{
          dataTestId: "back-scheduling",
          label: "Back",
          variant: "secondary",
          tone: "neutral",
          onClick: onBack,
        }}
        continueButton={{
          dataTestId: "next-scheduling",
          label: "Next",
          variant: "primary",
          onClick: handleNext,
          isDisabled: !canProceed(),
        }}
      />
    </div>
  );
};

export default SchedulingStep;
